import React from 'react';

import { ModerationModel, MultipleThresholds } from '../../moderationFiltersTypes';
import RangeSlider from '../../../../../../common/inputs/range-slider';
import {
  HIGH_NUM_THRESHOLD,
  HIGH_PERCENTAGE_THRESHOLD,
  LOW_NUM_THRESHOLD,
  LOW_PERCENTAGE_THRESHOLD,
  MEDIUM_NUM_THRESHOLD,
  MEDIUM_PERCENTAGE_THRESHOLD
} from '../../moderationFiltersConstants';
import Icon from 'utils/components/icon';

import styles from './ModerationFiltersMultiThresholds.module.scss';

const STRENGTH_TITLES = ['Low', 'Medium', 'High'];
const DEFAULT_NUM_THRESHOLDS = [LOW_NUM_THRESHOLD, MEDIUM_NUM_THRESHOLD, HIGH_NUM_THRESHOLD];
const DEFAULT_PERCENTAGE_THRESHOLDS = [
  LOW_PERCENTAGE_THRESHOLD,
  MEDIUM_PERCENTAGE_THRESHOLD,
  HIGH_PERCENTAGE_THRESHOLD
];

interface ModerationFiltersMultiThresholdsProps {
  isSuperAdmin: boolean;
  value: MultipleThresholds;
  currentModel: string;
  disabled: boolean;
  onConfigChange: (tag: string, value: MultipleThresholds) => void;
}

export function ModerationFiltersMultiThresholds(props: ModerationFiltersMultiThresholdsProps) {
  const { isSuperAdmin, value, disabled, currentModel, onConfigChange } = props;
  const { publishAndModerate, requireApproval, reject } = value;

  const isPercentage = currentModel !== ModerationModel.Incivility;
  const config = isPercentage ? defaultConfig : incivilityConfig;
  const { maximum, defaultThresholds, getStrengthValue } = config;

  const strengthValue = getStrengthValue(requireApproval);

  const onStrengthChange = (value: number) => {
    const newThresholds = { ...defaultThresholds[value] };

    onConfigChange(currentModel, newThresholds);
  };

  const onSliderChange = (propValue: number, prop: keyof MultipleThresholds) => {
    const newThresholds = { ...value, [prop]: propValue };

    onConfigChange(currentModel, newThresholds);
  };

  return (
    <div className={styles.container}>
      <RangeSlider
        displayValue={false}
        title={STRENGTH_TITLES[strengthValue]}
        value={strengthValue}
        maximum={2}
        onChange={onStrengthChange}
        disabled={disabled}
        isValuePercentage={false}
      />
      {isSuperAdmin && (
        <div className={styles.superAdminSliders}>
          <Icon name={'eye-disabled'} className={styles.eyeDisabledIcon} />
          <span>Super admins view only</span>
          <RangeSlider
            className={styles.rangeSlider}
            title={'Publish & Moderate'}
            value={publishAndModerate}
            disabled={disabled}
            onChange={value => onSliderChange(value, 'publishAndModerate')}
            isValuePercentage={isPercentage}
            maximum={maximum}
          />
          <RangeSlider
            className={styles.rangeSlider}
            title={'Require Approval'}
            value={requireApproval}
            disabled={disabled}
            onChange={value => onSliderChange(value, 'requireApproval')}
            isValuePercentage={isPercentage}
            maximum={maximum}
          />
          <RangeSlider
            className={styles.rangeSlider}
            title={'Reject'}
            value={reject}
            disabled={disabled}
            onChange={value => onSliderChange(value, 'reject')}
            isValuePercentage={isPercentage}
            maximum={maximum}
          />
        </div>
      )}
    </div>
  );
}

interface ModelThresholdsConfig {
  maximum: number;
  defaultThresholds: Array<MultipleThresholds>;
  getStrengthValue: (value: number) => number;
}

const getStrength = (limits: [number, number, number], inversed = false) => (value: number) => {
  const maxIndex = limits.length - 1;

  for (let i = 0; i < limits.length; i++) {
    if (value <= limits[i]) {
      return inversed ? i : maxIndex - i;
    }
  }

  return 0;
};

const getStrengthFromPercentage = getStrength([95, 98, 100]);
const getStrengthFromNums = getStrength([3, 5, 10]);

const incivilityConfig: ModelThresholdsConfig = {
  maximum: 10,
  defaultThresholds: DEFAULT_NUM_THRESHOLDS,
  getStrengthValue: getStrengthFromNums
};

const defaultConfig: ModelThresholdsConfig = {
  maximum: 100,
  defaultThresholds: DEFAULT_PERCENTAGE_THRESHOLDS,
  getStrengthValue: getStrengthFromPercentage
};
